import { normalizeError } from "@/api/client";
import type { AppError } from "@/types";

/**
 * Centralised error handling for API-driven views.
 *
 * Accepts either a raw Axios error or an already-normalised error object and
 * returns a user-friendly message. Distinguishes network, timeout, expired
 * token, validation and generic server errors.
 */

export function toAppError(error: unknown): AppError {
  // Already normalised by the axios interceptor?
  const candidate = error as Partial<AppError>;
  if (candidate && candidate.type && typeof candidate.message === "string") {
    return candidate as AppError;
  }
  return normalizeError(error);
}

export function getErrorMessage(error: unknown): string {
  const appError = toAppError(error);

  switch (appError.type) {
    case "timeout":
      return "زمان درخواست به پایان رسید. اتصال خود را بررسی کنید و دوباره تلاش کنید.";
    case "network":
      return "دسترسی به سرور ممکن نیست. مطمئن شوید سرور در حال اجراست.";
    case "unauthorized":
      return unauthorizedMessage(appError);
    case "forbidden":
      return appError.message || "شما مجوز انجام این کار را ندارید.";
    case "not_found":
      return appError.message || "منبع درخواستی یافت نشد.";
    case "validation":
      if (appError.fieldErrors && appError.fieldErrors.length) {
        return appError.fieldErrors
          .map((f) => `${f.field}: ${f.message}`)
          .join("\n");
      }
      return appError.message || "برخی فیلدها نامعتبر هستند.";
    default:
      return appError.message || "خطای غیرمنتظره‌ای رخ داد.";
  }
}

const SESSION_EXPIRED = "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.";

function unauthorizedMessage(appError: AppError): string {
  const raw = (appError.message || "").trim().toLowerCase();
  if (
    raw.includes("incorrect") ||
    raw.includes("password") ||
    raw.includes("mobile number")
  ) {
    return "شماره موبایل یا رمز عبور نادرست است.";
  }
  if (raw.includes("inactive")) {
    return "حساب کاربری غیرفعال است.";
  }
  if (raw.includes("no account")) {
    return "حسابی با این شماره موبایل پیدا نشد.";
  }
  if (
    !raw ||
    raw === "unauthorized" ||
    raw === "مشکلی پیش آمد." ||
    raw.includes("token") ||
    raw.includes("expired")
  ) {
    return SESSION_EXPIRED;
  }
  return appError.message;
}

export function isUnauthorized(error: unknown): boolean {
  return toAppError(error).type === "unauthorized";
}

export function isNetworkError(error: unknown): boolean {
  const t = toAppError(error).type;
  return t === "network" || t === "timeout";
}

// Convenience for logging + returning a message in one call.
export function handleError(error: unknown, context = ""): string {
  const appError = toAppError(error);
  if (context) {
    // eslint-disable-next-line no-console
    console.error(`[${context}]`, appError.message, appError.original || "");
  }
  return getErrorMessage(appError);
}
